import axiosLib, { type AxiosInstance } from 'axios'

export interface BackendEvent {
  readonly id: number
  readonly title: string
  readonly startsAt: string
  readonly endsAt: string
  readonly active?: boolean
  readonly timezone?: string
  readonly updatedAt?: string
  readonly isPinned?: boolean
  readonly imageUrl?: string
  readonly presaleStart?: string
  readonly presaleEnd?: string
  readonly presaleType?: 'none' | 'static' | 'one_time'
  readonly generalSaleStart?: string
  readonly venue?: {
    readonly name?: string
    readonly address?: {
      readonly addressLine1?: string
      readonly addressLine2?: string
      readonly city?: string
      readonly state?: string
      readonly zipCode?: string
      readonly country?: string
    }
  }
  readonly fanMeetAndGreet?: {
    readonly enabled?: boolean
    readonly count?: number
    readonly fanMeetAndGreetButtonText?: string
    readonly fanMeetAndGreetStart?: string
    readonly fanMeetAndGreetEnd?: string
    readonly fanMeetAndGreetDialogJoinText?: string
    readonly fanMeetAndGreetDialogLeaveText?: string
  }
  readonly features?: {
    readonly presale?: {
      readonly url?: string
      readonly code?: string
    }
    readonly earlyAccess?: {
      readonly count?: number
      readonly buttonTitle?: string
      readonly maxRsvp?: number
      readonly rsvpStart?: string
      readonly rsvpEnd?: string
      readonly rsvpInfo?: string
      readonly rsvpLeaveInfo?: string
    }
    readonly ticketSales?: {
      readonly url?: string
      readonly buttonTitle?: string
    }
    readonly vipTicketSales?: {
      readonly url?: string
      readonly buttonTitle?: string
    }
  }
  // ChorusCommonFields
  readonly publishAt?: string | null
  readonly createdAt?: string
}

export interface AttendeeRecord {
  readonly id: number
  readonly displayName?: string
  readonly email?: string
  readonly profileImageUrl?: string | null
  readonly venueName?: string
}

interface AttendeeListResponse {
  readonly status: string
  readonly data: AttendeeRecord[]
}

export interface PresaleCodePoolWithEvents {
  readonly id: number
  readonly name: string
  readonly eventIds: number[]
  readonly presale_start: string | null
  readonly presale_end: string | null
  readonly presale_reveal: string | null
  readonly presale_hide: string | null
  readonly general_sale_start: string | null
  readonly timezone: string | null
}

export interface PresalePoolDates {
  presaleStart?: string
  presaleEnd?: string
  presaleReveal?: string
  presaleHide?: string
  generalSaleStart?: string
  timezone?: string
}

export interface PresaleDashboard {
  readonly artistId: number
  readonly pool: {
    readonly id: number
    readonly name: string | null
    readonly stats: { readonly total: number; readonly reserved: number; readonly available: number }
  } | null
  readonly config: {
    readonly presaleStart: string | null
    readonly presaleEnd: string | null
    readonly presaleCodeRevealDate: string | null
    readonly presaleCodeHideDate: string | null
    readonly generalSaleStart: string | null
  }
}

export interface CreateEventPayload {
  readonly title: string
  readonly startDate: string
  readonly endDate: string
  readonly venueName: string
  readonly address: {
    readonly addressLine1: string
    readonly addressLine2?: string
    readonly city: string
    readonly state: string
    readonly country: string
    readonly zipCode: string
  }
  readonly eventCategoryId: number
  readonly timezone?: string
  readonly isPinned?: boolean
  readonly publishAt?: string
  readonly disabledEarlyAccess?: boolean
  readonly rsvpButtonText?: string
  readonly rsvpStart?: string
  readonly rsvpEnd?: string
  readonly rsvpInfo?: string
  readonly rsvpLeaveInfo?: string
  readonly rsvpCapacity?: number
  readonly presaleUrl?: string
  readonly presaleCode?: string
  readonly presaleStart?: string
  readonly presaleEnd?: string
  readonly linkButtonText?: string
  readonly vipUrl?: string
  readonly vipButtonText?: string
  readonly fanMeetAndGreetStart?: string
  readonly fanMeetAndGreetEnd?: string
  readonly fanMeetAndGreetDialogJoinText?: string
  readonly fanMeetAndGreetDialogLeaveText?: string
  readonly meetButtonText?: string
  readonly imageUrl?: string
}

export type UpdateEventPayload = Partial<Omit<CreateEventPayload, 'eventCategoryId'>> & {
  readonly eventCategoryId?: number
}

export interface BulkRowError {
  readonly row: number
  readonly field: string
  readonly message: string
  readonly title?: string
}

export interface BulkEventPreview {
  readonly row: number
  readonly title: string
  readonly venueName?: string | null
  readonly city: string
  readonly state: string
  readonly country: string
  readonly startDate?: string
}

interface BulkEventsValidationResponse {
  readonly status: string
  readonly dryRun: boolean
  readonly data: BulkEventPreview[]
  readonly summary: { readonly total_valid: number }
}

export interface BulkValidationResult {
  readonly valid: boolean
  readonly preview: BulkEventPreview[]
  readonly summary: { readonly total_valid: number } | null
  readonly errors: BulkRowError[]
  readonly message?: string
}

interface BulkEventsCreationResponse {
  readonly status: string
  readonly dryRun: false
  readonly data: BackendEvent[]
  readonly summary: { readonly total_created: number }
}

export interface BulkCreationResult {
  readonly valid: boolean
  readonly created: BackendEvent[]
  readonly summary: { readonly total_created: number } | null
  readonly errors: BulkRowError[]
  readonly message?: string
}

interface PresaleCodesValidationResponse {
  readonly status: string
  readonly dryRun: boolean
  readonly data: {
    readonly totalCodes: number
    readonly duplicatesRemoved: number
    readonly sampleCodes: string[]
  }
}

export interface PresaleValidationResult {
  readonly valid: boolean
  readonly data: PresaleCodesValidationResponse['data'] | null
  readonly errors: string[]
  readonly message?: string
}

interface EventsListResponse {
  readonly status: string
  readonly data: {
    readonly valid_events: BackendEvent[]
    readonly errored_events: unknown[]
  }
  readonly summary: {
    readonly total_events: number
    readonly valid_events: number
    readonly errored_events: number
  }
}

interface ClubResponse {
  readonly club: { readonly id: number }
}

interface StrapiJwtResponse {
  readonly jwt: string
}

export class AdminEventsApiDataSource {
  private clubIdCache = new Map<string, number>()
  private strapiJwt: string | null = null
  private readonly backendAxios: AxiosInstance

  constructor(
    private readonly baseUrl: string,
    private readonly clerkAxios: AxiosInstance,
    private readonly getOrganizationSlug: () => string | undefined
  ) {
    this.backendAxios = axiosLib.create()
  }

  private async getStrapiJwt(): Promise<string> {
    if (this.strapiJwt) return this.strapiJwt
    const response = await this.clerkAxios.post<StrapiJwtResponse>(`${this.baseUrl}/clerk/generate-strapi-jwt`)
    this.strapiJwt = response.data.jwt
    return this.strapiJwt
  }

  private async authHeaders(): Promise<Record<string, string>> {
    const jwt = await this.getStrapiJwt()
    return { Authorization: `Bearer ${jwt}` }
  }

  private async resolveClubId(): Promise<number> {
    const slug = this.getOrganizationSlug()
    if (!slug) throw new Error('No organization selected')

    const cached = this.clubIdCache.get(slug)
    if (cached !== undefined) return cached

    const response = await this.backendAxios.get<ClubResponse>(`${this.baseUrl}/clubs/slug/${slug}`)
    const clubId = response.data.club.id
    this.clubIdCache.set(slug, clubId)
    return clubId
  }

  async getEvents(): Promise<BackendEvent[]> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    const response = await this.backendAxios.get<EventsListResponse>(`${this.baseUrl}/clubs/${clubId}/events/admin`, {
      headers
    })
    return response.data.data.valid_events
  }

  async getRsvpList(eventId: number): Promise<AttendeeRecord[]> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    const response = await this.backendAxios.get<AttendeeListResponse>(
      `${this.baseUrl}/clubs/${clubId}/events/${eventId}/rsvp/list`,
      { headers }
    )
    return response.data.data
  }

  async getMeetAndGreetList(eventId: number): Promise<AttendeeRecord[]> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    const response = await this.backendAxios.get<AttendeeListResponse>(
      `${this.baseUrl}/clubs/${clubId}/events/${eventId}/meetAndGreet/list`,
      { headers }
    )
    return response.data.data
  }

  async getEventById(eventId: number): Promise<BackendEvent | null> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    try {
      const response = await this.backendAxios.get<{ status: string; data: BackendEvent }>(
        `${this.baseUrl}/clubs/${clubId}/events/${eventId}`,
        { headers }
      )
      return response.data.data
    } catch {
      return null
    }
  }

  async createEvent(payload: CreateEventPayload): Promise<BackendEvent> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    const response = await this.backendAxios.post<{ status: string; data: BackendEvent }>(
      `${this.baseUrl}/clubs/${clubId}/events`,
      payload,
      { headers }
    )
    return response.data.data
  }

  async updateEvent(eventId: number, payload: UpdateEventPayload): Promise<BackendEvent> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    const response = await this.backendAxios.put<{ status: string; data: BackendEvent }>(
      `${this.baseUrl}/clubs/${clubId}/events/${eventId}`,
      payload,
      { headers }
    )
    return response.data.data
  }

  async uploadEventImage(eventId: number, file: File): Promise<{ fileId: number; imageUrl: string }> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    const formData = new FormData()
    formData.append('image', file)
    const response = await this.backendAxios.post<{ status: string; data: { fileId: number; imageUrl: string } }>(
      `${this.baseUrl}/clubs/${clubId}/events/${eventId}/image`,
      formData,
      { headers }
    )
    return response.data.data
  }

  async deleteEvent(eventId: number): Promise<void> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    await this.backendAxios.delete(`${this.baseUrl}/clubs/${clubId}/events/${eventId}`, { headers })
  }

  async downloadBulkTemplate(format: 'csv' | 'xlsx' = 'csv'): Promise<void> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    const response = await this.backendAxios.get(
      `${this.baseUrl}/clubs/${clubId}/events/bulk/template?format=${format}`,
      { headers, responseType: 'blob' }
    )
    const filename = format === 'xlsx' ? 'event-bulk-template.xlsx' : 'event-bulk-template.csv'
    const url = URL.createObjectURL(response.data as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  // Downloads the club members CSV: one row per member with their latest presale code.
  async exportMembersPresaleCodes(): Promise<void> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    const response = await this.backendAxios.get(`${this.baseUrl}/clubs/${clubId}/presales/export`, {
      headers,
      responseType: 'blob'
    })
    const date = new Date().toISOString().slice(0, 10)
    const url = URL.createObjectURL(response.data as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${this.getOrganizationSlug() ?? clubId}-members-presale-codes-${date}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  async validateBulkEvents(file: File): Promise<BulkValidationResult> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    const formData = new FormData()
    formData.append('file', file)
    formData.append('dryRun', 'true')
    try {
      const response = await this.backendAxios.post<BulkEventsValidationResponse>(
        `${this.baseUrl}/clubs/${clubId}/events/bulk`,
        formData,
        { headers }
      )
      return { valid: true, preview: response.data.data, summary: response.data.summary, errors: [] }
    } catch (e: unknown) {
      if (e && typeof e === 'object' && 'response' in e) {
        const resp = (e as { response: { data: { message?: string; errors?: BulkRowError[] } } }).response
        return {
          valid: false,
          preview: [],
          summary: null,
          errors: resp.data.errors ?? [],
          message: resp.data.message
        }
      }
      return { valid: false, preview: [], summary: null, errors: [], message: 'Network error' }
    }
  }

  async createBulkEvents(file: File): Promise<BulkCreationResult> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    const formData = new FormData()
    formData.append('file', file)
    formData.append('dryRun', 'false')
    try {
      const response = await this.backendAxios.post<BulkEventsCreationResponse>(
        `${this.baseUrl}/clubs/${clubId}/events/bulk`,
        formData,
        { headers }
      )
      return { valid: true, created: response.data.data, summary: response.data.summary, errors: [] }
    } catch (e: unknown) {
      if (e && typeof e === 'object' && 'response' in e) {
        const resp = (e as { response: { data: { message?: string; errors?: BulkRowError[] } } }).response
        return {
          valid: false,
          created: [],
          summary: null,
          errors: resp.data.errors ?? [],
          message: resp.data.message
        }
      }
      return { valid: false, created: [], summary: null, errors: [], message: 'Network error' }
    }
  }

  async validatePresaleCodes(file: File, name: string): Promise<PresaleValidationResult> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    const formData = new FormData()
    formData.append('presale-codes', file)
    formData.append('name', name)
    formData.append('dryRun', 'true')
    try {
      const response = await this.backendAxios.post<PresaleCodesValidationResponse>(
        `${this.baseUrl}/clubs/${clubId}/presales`,
        formData,
        { headers }
      )
      return { valid: true, data: response.data.data, errors: [] }
    } catch (e: unknown) {
      if (e && typeof e === 'object' && 'response' in e) {
        const resp = (e as { response: { data: { message?: string } } }).response
        return { valid: false, data: null, errors: [], message: resp.data.message }
      }
      return { valid: false, data: null, errors: [], message: 'Network error' }
    }
  }

  async createPresalePool(file: File, name: string, dates?: PresalePoolDates): Promise<{ poolId: number }> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    const formData = new FormData()
    formData.append('presale-codes', file)
    formData.append('name', name)
    if (dates?.presaleStart) formData.append('presaleStart', dates.presaleStart)
    if (dates?.presaleEnd) formData.append('presaleEnd', dates.presaleEnd)
    if (dates?.presaleReveal) formData.append('presaleReveal', dates.presaleReveal)
    if (dates?.presaleHide) formData.append('presaleHide', dates.presaleHide)
    if (dates?.generalSaleStart) formData.append('generalSaleStart', dates.generalSaleStart)
    if (dates?.timezone) formData.append('timezone', dates.timezone)
    const response = await this.backendAxios.post<{ dryRun: false; codePool: { poolId: number } }>(
      `${this.baseUrl}/clubs/${clubId}/presales`,
      formData,
      { headers }
    )
    return { poolId: response.data.codePool.poolId }
  }

  async assignPoolToEvent(
    poolId: number,
    eventId: number,
    dates?: { presaleStart?: string; presaleEnd?: string; generalSaleStart?: string }
  ): Promise<void> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    await this.backendAxios.post(
      `${this.baseUrl}/clubs/${clubId}/presales/${poolId}/assignToEvent/${eventId}`,
      dates ?? {},
      { headers }
    )
  }

  async unassignPoolFromEvent(poolId: number, eventId: number): Promise<void> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    await this.backendAxios.delete(`${this.baseUrl}/clubs/${clubId}/presales/${poolId}/unassignFromEvent/${eventId}`, {
      headers
    })
  }

  async getEventCategories(): Promise<{ id: number; name: string }[]> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    const response = await this.backendAxios.get<{ status: string; data: { id: number; name: string }[] }>(
      `${this.baseUrl}/clubs/${clubId}/event-categories`,
      { headers }
    )
    return response.data.data
  }

  async getPresaleCodePools(): Promise<PresaleCodePoolWithEvents[]> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    const response = await this.backendAxios.get<PresaleCodePoolWithEvents[]>(
      `${this.baseUrl}/clubs/${clubId}/presales`,
      { headers }
    )
    return response.data
  }

  async updatePresalePoolDates(poolId: number, dates: PresalePoolDates): Promise<void> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    await this.backendAxios.put(`${this.baseUrl}/clubs/${clubId}/presales/${poolId}`, dates, { headers })
  }

  async getPresaleDashboard(): Promise<PresaleDashboard | null> {
    const [clubId, headers] = await Promise.all([this.resolveClubId(), this.authHeaders()])
    const response = await this.backendAxios.get<PresaleDashboard>(`${this.baseUrl}/admin/presales/upcoming/json`, {
      headers,
      params: { artistId: clubId }
    })
    return response.data
  }
}
